import { Pagination } from "@mui/material";
import {
  AvatarIcon,
  CalendarIcon,
  PersonIcon,
  SewingPinFilledIcon,
} from "@radix-ui/react-icons";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { SearchInput } from "../../components/Widget/SearchInput";
import { find } from "../../services/service";
import { useStoreApp } from "../../store/storeApp";
import { useStoreCommune } from "../../store/storeCommune";
import {
  LISTE_ATTRIBUE_EQUIPE_MAINTENANCE,
  LISTE_COMMUNE_SANS_PAGINATION,
  PRESTATAIRE,
  PRIORITE,
} from "../../Utils/constant";
import { checkTagValidity } from "../../Utils/Utils";
import { StatusMaintenanceModal } from "./StatusMaintenanceModal";

export const getPrioriteBadge = (priorite) => {
  const baseClasses = "px-2 py-1 text-xs font-medium rounded-full";
  switch (priorite) {
    case PRIORITE.CRITIQUE:
      return `${baseClasses} bg-red-100 text-red-800`;
    case PRIORITE.HAUTE:
      return `${baseClasses} bg-yellow-100 text-yellow-800`;
    case PRIORITE.MOYENNE:
      return `${baseClasses} bg-blue-100 text-blue-800`;
    case PRIORITE.BASSE:
      return `${baseClasses} bg-green-100 text-green-800`;
    default:
      return `${baseClasses} bg-gray-100 text-gray-800`;
  }
};

const EquipeMaintenance = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [dataListe, setDataListe] = useState({
    data: [],
    loading: false,
  });
  const prestataire = useStoreApp();
  const communeStore = useStoreCommune();

  const getData = async (page, param, status = "") => {
    setDataListe({
      data: [],
      loading: true,
    });
    await find(
      `${LISTE_ATTRIBUE_EQUIPE_MAINTENANCE}?page=${page}&param=${param}&size=8&statut=${status}`,
    ).then((res) => {
      setDataListe({
        data: res.data?.donnee,
        loading: false,
      });
    });
  };
  // Fonction de recherche avec debounce
  const handleSearch = (value) => {
    setSearchTerm(value);
    setIsSearching(true);
    getData(0, value).finally(() => setIsSearching(false));
  };

  const moreEnAttente = (page) => {
    getData(page - 1, searchTerm);
  };

  const handleUpdate = () => {
    // Refresh the dossiers list when status is updated
    getData(0, searchTerm);
    toast.success("Statut modifié avec succès");
  };

  useEffect(() => {
    prestataire.getAll(PRESTATAIRE);
    communeStore.getData(`${LISTE_COMMUNE_SANS_PAGINATION}`);
  }, []);

  useEffect(() => {
    getData(0, "");
  }, []);

  return (
    <div className="">
      {/* Header */}

      <div className="bg-white rounded-md px-2 py-4 mt-3">
        {/* Filtres */}
        <div className="flex items-center gap-3 mb-4">
          <SearchInput
            onChange={(e) => handleSearch(e.target.value)}
            value={searchTerm}
            isSearching={isSearching}
          />
          <select
            onChange={(e) => {
              getData(0, searchTerm, e.target.value);
            }}
            className="select select-bordered"
          >
            <option value="">Sélectionner un statut</option>
            <option value="EN_ATTENTE_DE_TRAITEMENT">En attente</option>
            <option value="RESOLUE">Résolue</option>
            <option value="FERMEE">Fermée</option>
          </select>
        </div>

        {/* Grille des rendez-vous */}
        {dataListe?.loading ? (
          <div className="flex flex-col justify-center items-center py-20 space-y-3">
            <Loader2 className="w-8 h-8 animate-spin text-main" />
            <span className="ml-3 text-gray-600 text-sm">
              Chargement des données...
            </span>
          </div>
        ) : dataListe?.data?.data?.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-sm text-gray-500">Aucune donnée disponible</p>
          </div>
        ) : (
          <>
            <div className="grid lg:grid-cols-3 md:grid-cols-3 sm:grid-cols-2 mt-4 grid-cols-1 gap-5">
              {dataListe?.data?.data?.map((item) => (
                <div
                  key={item.id}
                  className={`relative bg-white border  rounded-xl shadow-sm hover:shadow-md transition-shadow`}
                >
                  <div className="rounded-t-xl flex items-center gap-3 border-b border-dashed border-amber-400 px-2 py-3">
                    <p className="text-[12px] font-semibold">N° {item?.id}</p>
                    <span className={getPrioriteBadge(item.priorite)}>
                      {item.priorite}
                    </span>
                  </div>
                  <div className="px-3 py-2">
                    <div className="items-center gap-2 space-y-3">
                      <div className="flex gap-1 my-3">
                        <PersonIcon className="bg-gray-100 rounded-full w-8 h-8 p-2" />
                        <div>
                          <p className="text-[13px] font-medium">
                            {item?.nomPrenomsClient}
                          </p>
                          <p className="text-xs flex font-semibold gap-2">
                            <span>{item?.contactClient}</span>
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <div className=" w-fit px-1 py-[2px]">
                          {item?.dateCreation && (
                            <p className="text-xs flex gap-1 items-center font-medium">
                              <CalendarIcon />
                              {new Date(item?.dateCreation).toLocaleDateString(
                                "fr-FR",
                                {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "2-digit",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                },
                              )}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="gap-1">
                        <div>
                          <p className="text-[13px] font-medium">
                            Numero FTTH : {item?.numeroFtth}
                          </p>
                        </div>
                      </div>

                      <h1 className="text-xs flex gap-1 items-center my-2">
                        <AvatarIcon />
                        Rapporteur : {item?.rapporteur?.agent?.nomPrenoms}
                      </h1>
                      {item.description && (
                        <div className="dropdown flex items-center">
                          <p className="text-xs">{item?.description}</p>
                        </div>
                      )}
                    </div>

                    {item?.commune && (
                      <h1 className="my-2 text-[13px] font-medium flex items-center">
                        <SewingPinFilledIcon />
                        {item?.commune?.designation}
                      </h1>
                    )}

                    {item?.equipe && (
                      <div className="pt-2">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700">
                          Équipe: {item?.equipe?.designation}
                        </span>
                      </div>
                    )}

                    {item?.raison && (
                      <div className="my-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-xs text-red-700 font-semibold">
                          Raison:
                        </p>
                        <p className="text-xs text-red-600 mt-1">
                          {item?.raison}
                        </p>
                      </div>
                    )}

                    <div className="flex items-center gap-1">
                      <p className="bg-blue-50 text-blue-900 py-1 px-1 rounded-lg font-medium text-xs truncate">
                        {item?.statut?.toLowerCase().replace(/_/g, " ")}
                      </p>
                    </div>

                    <div className="flex gap-2 mt-3">
                      {checkTagValidity("MODIFIER_STATUT_REMONTEE") && (
                        <StatusMaintenanceModal
                          item={item}
                          onStatusUpdate={handleUpdate}
                        />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        <div className="my-3 flex justify-end">
          <Pagination
            onChange={(event, newValue) => moreEnAttente(newValue)}
            count={dataListe?.data?.totalPages}
            variant="outlined"
            color="primary"
            shape="rounded"
          />
        </div>
      </div>
    </div>
  );
};

export default EquipeMaintenance;
